import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearNotificationRule,
  deleteNotificationGroup,
  listNotificationGroups,
  listNotificationRules,
  listRoutingPeople,
  resolveRoutingPeople,
  saveNotificationGroup,
  setNotificationRule,
  type Channel,
  type SaveGroupInput,
} from '../api/notificationRouting';

/** Every query here starts with this, so one write refreshes the whole screen. */
export const NOTIFICATION_ROUTING_KEY = ['notification-routing'] as const;

const scopeKey = (eventId: string | null) => eventId ?? 'office';

export function useNotificationGroups(eventId: string | null) {
  return useQuery({
    queryKey: [...NOTIFICATION_ROUTING_KEY, scopeKey(eventId), 'groups'],
    queryFn: () => listNotificationGroups(eventId),
  });
}

export function useNotificationRules(eventId: string | null) {
  return useQuery({
    queryKey: [...NOTIFICATION_ROUTING_KEY, scopeKey(eventId), 'rules'],
    queryFn: () => listNotificationRules(eventId),
  });
}

export function useRoutingPeople(eventId: string | null) {
  return useQuery({
    queryKey: [...NOTIFICATION_ROUTING_KEY, scopeKey(eventId), 'people'],
    queryFn: () => listRoutingPeople(eventId),
  });
}

/** Names for members the people list no longer offers, asked only when there are any. */
export function useResolvedPeople(eventId: string | null, ids: string[]) {
  const sorted = [...ids].sort();
  return useQuery({
    queryKey: [...NOTIFICATION_ROUTING_KEY, scopeKey(eventId), 'resolve', sorted.join(',')],
    queryFn: () => resolveRoutingPeople(eventId, sorted),
    enabled: sorted.length > 0,
  });
}

function useRefreshAfter<TVars, TData>(mutationFn: (vars: TVars) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation<TData, Error, TVars>({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NOTIFICATION_ROUTING_KEY }),
  });
}

export function useSaveNotificationGroup() {
  return useRefreshAfter((input: SaveGroupInput) => saveNotificationGroup(input));
}

export function useDeleteNotificationGroup() {
  return useRefreshAfter((id: string) => deleteNotificationGroup(id));
}

export function useSetNotificationRule() {
  return useRefreshAfter(
    (input: {
      eventId: string | null;
      notification: string;
      groupId: string;
      channels: Channel[];
    }) => setNotificationRule(input),
  );
}

export function useClearNotificationRule() {
  return useRefreshAfter((input: { eventId: string | null; notification: string }) =>
    clearNotificationRule(input),
  );
}
